import React, { useEffect, useRef, useState, SyntheticEvent, KeyboardEvent, MouseEvent } from 'react';
import { CSSTransition } from 'react-transition-group';
import { manageDropdownListener } from '@helpers/use_effect_callbacks';

import './styles/main.scss';

interface DropdownWrapperProps {
  children: JSX.Element;
  dropdownContents: JSX.Element;
  close?: boolean;
  closeCallback?: () => void;
  cssTransitionClasses?: string;
  openOnClick?: boolean;
  openOnHover?: boolean;
  wrapperClassnames?: string;
}

const DropdownWrapper = ({
  children,
  dropdownContents,
  close = false,
  closeCallback = undefined,
  cssTransitionClasses = '',
  openOnClick = true,
  openOnHover = true,
  wrapperClassnames = '',
}: DropdownWrapperProps): JSX.Element => {
  const [showDropdown, setShowDropdown] = useState(false);
  const menuRef = useRef(null);

  const { device } = BFG.currentUser;
  const isTouchScreen = device === 'mobile-view' || device === 'tablet-view';

  const handleOpenAndClose = (e: any): void => {
    switch (e.type) {
      case 'keypress':
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') setShowDropdown((prevState) => !prevState);
        break;
      case 'click':
        if (openOnClick || isTouchScreen) setShowDropdown((prevState) => !prevState);
        break;
      case 'mouseenter':
      case 'mouseleave':
        if (openOnHover && !isTouchScreen) setShowDropdown(e.type === 'mouseenter');
        break;
      default:
    }
  };

  useEffect(() => setShowDropdown(false), []); // close dropdown when unmounting

  useEffect(() => (
    manageDropdownListener(menuRef, showDropdown, setShowDropdown)
  ), [menuRef, showDropdown, setShowDropdown]);

  useEffect(() => {
    if (close) {
      setShowDropdown(false); // allows consumer/parent component to close dropdown
      closeCallback();
    }
  }, [close]);

  return (
    <div
      className={`bf-dropdown-wrapper ${wrapperClassnames} ${showDropdown === true ? 'open' : ''}`}
      onMouseEnter={handleOpenAndClose}
      onMouseLeave={handleOpenAndClose}
      ref={menuRef}
    >
      <div
        className="selected-option-click-container"
        onClick={handleOpenAndClose}
        onKeyPress={handleOpenAndClose}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>
      <CSSTransition
        classNames={cssTransitionClasses}
        in={showDropdown}
        timeout={200}
        unmountOnExit
      >
        {dropdownContents}
      </CSSTransition>
    </div>
  );
};

export default DropdownWrapper;
